import { ResolvableLiteral, StateService, Transition, UIRouter } from '@uirouter/angular';
import { HttpErrorResponse } from '@angular/common/http';
import { filter, take } from 'rxjs/operators';

import { AccountLicenseService } from 'rev-shared/security/AccountLicense.Service';
import { AppBootstrapService } from 'rev-shared/bootstrap/AppBootstrap.Service';
import { BootstrapContext } from 'rev-shared/bootstrap/BootstrapContext';
import { IAccountLicense } from 'rev-shared/security/IAccountLicense';
import { IMediaFeatures } from 'rev-shared/media/IMediaFeatures';
import { IVbNg2StateDeclaration } from 'rev-shared/ts-utils/IVbNg2StateDeclaration';
import { IVideoPlaybackConfig } from 'rev-portal/media/videos/videoPlayback/IVideoPlaybackConfig';
import { LmsMessageBus } from 'rev-shared/embed/LmsMessageBus.Service';
import { LoginRedirectService } from 'rev-shared/security/LoginRedirect.Service';
import { MediaFeaturesService } from 'rev-shared/media/MediaFeatures.Service';
import { PORTAL_VIDEO_PLAYBACK_STATE } from 'rev-shared/bootstrap/AppStateNames';
import { PlaylistService } from 'rev-shared/media/Playlist.Service';
import { PublicVideoPasswordComponent } from 'rev-shared/media/publicVideoPassword/PublicVideoPassword.Component';
import { ThemeService } from 'rev-portal/branding/Theme.Service';
import { UiViewContainerComponent } from 'rev-shared/ui/uiViewContainer/UiViewContainer.Component';
import { Unauthorized401Component } from 'rev-shared/security/components/Unauthorized401.Component';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { UserLocalIPService } from 'rev-shared/security/UserLocalIP.Service';
import { VbCenteredSpinnerComponent } from 'rev-shared/ui/spinner/VbCenteredSpinner.Component';
import { VideoService } from 'rev-shared/media/Video.Service';
import { decodeQueryString } from 'rev-shared/util/Util.Service';
import { defaultColors } from 'rev-portal/branding/Defaults';
import { firstValueFrom } from 'rev-shared/rxjs/firstValueFrom';
import { getPortalBaseStates } from 'rev-portal/PortalBaseState.Config';
import { parseUrlTimespan } from 'rev-shared/date/DateParsers.Service';
import { publishParentMessage } from 'rev-shared/security/EmbedMessageBus';

import { IEmbedStartup, EMBED_STARTUP_INJECTION_TOKEN } from './EmbedStartup.Provider';
import { EmbedVideo } from './video/EmbedVideo.Component';
import { getCommonSecurityStatesNg2 } from '../shared/security/components/SecurityComponentsState.Config';
import { EmbedLoginComponent } from './login/EmbedLogin.Component';
import { EmbedPlaylistComponent, EmbedPlaylistSliderComponent } from './playlist/EmbedPlaylist.Component';
import { EmbedPlaylistGridComponent } from './playlist/EmbedPlaylistGrid.Component';
import { EmbedFullVodPlaybackComponent } from './video/EmbedFullVodPlayback.Component';
import { getEmbedPlayerConfig, parseBooleanQueryParam } from './EmbedUtil';

import loginStyles from './Embed.module.less';

const PUBLIC_VIDEO_PASSWORD_STATE_NAME: string = 'publicVideoPassword';
export const queryParams = decodeQueryString();

const initializationResolve: ResolvableLiteral[] = [
	{
		token: 'EmbedStartupResolve',
		deps: [
			EMBED_STARTUP_INJECTION_TOKEN
		],
		resolveFn: (EmbedStartup: IEmbedStartup) => EmbedStartup.$promise
	},
	{
		token: 'loginStyles',
		resolveFn: () => loginStyles
	},
	{
		token: 'thumbnailUri',
		resolveFn: () => BootstrapContext.thumbnailUri
	}
];

const sessionResolve = {
	token: 'embedVideoSession',
	deps: ['videoId', VideoService, UserContextService],
	resolveFn(videoId: string, VideoService: VideoService, UserContext: UserContextService) {
		// We ignore the video session for lms_scorm
		if(!videoId || !UserContext.isUserAuthenticated() || queryParams.viewContext === 'lms_scorm') {
			return;
		}
		return VideoService.getVideoSession(videoId);
	}
};

function getMainResolves(includeEmbedStartupResolve?: boolean): ResolvableLiteral[] {
	return [
		{
			token: 'accountId',
			deps: [UserContextService],
			resolveFn: (UserContext: UserContextService) => UserContext.getAccount().id
		},
		{
			token: 'playlistId',
			resolveFn: () => queryParams.playlist
		},
		{
			token: 'videoId',
			deps: [Transition],
			resolveFn: transition => BootstrapContext.videoId || transition.params().videoId
		},
		{
			token: 'accountLicense',
			deps: [AccountLicenseService, includeEmbedStartupResolve ? 'EmbedStartupResolve' : undefined].filter(Boolean),
			resolveFn: (accountLicense: AccountLicenseService) => accountLicense.reload()
		},
		{
			token: 'accountLicenseCheck',
			deps: [UserContextService, 'accountLicense'],
			resolveFn(UserContext: UserContextService, accountLicense: IAccountLicense) {
				if (!UserContext.isUserAuthenticated() && !accountLicense.mediaViewingAllowed) {
					return Promise.reject({ status: 401 });
				}
			}
		},
		sessionResolve,
		{
			token: 'params',
			deps: ['embedVideoSession'],
			resolveFn: embedVideoSession => {
				if (queryParams.startAt) {
					queryParams.startAt = typeof queryParams.startAt === 'number'
						? queryParams.startAt
						: parseUrlTimespan(queryParams.startAt);
				} else {
					queryParams.startAt = embedVideoSession?.time || 0;
				}
				return queryParams;
			}
		},
		{
			token: 'mediaFeatures',
			deps: ['accountId', MediaFeaturesService],
			resolveFn: (accountId: string, MediaFeatures: MediaFeaturesService) => MediaFeatures.getFeatures(accountId)
		},
	];
}

const params401 = {
	reason: {}
};

export function configureEmbedRouter(uiRouter: UIRouter): void {
	uiRouter.trace.enable('TRANSITION');

	if (!queryParams.playlist && queryParams.fullPlayer) {
		uiRouter.urlService.rules.otherwise({ state: 'embed' });
	} else if (queryParams.viewContext === 'lms_scorm') {
		uiRouter.urlService.rules.otherwise({ state: 'lmsEmbed' });
	}
	else {
		uiRouter.urlService.rules.otherwise('');
	}
}

export function getLmsEmbedStates(): IVbNg2StateDeclaration[] {
	return [
		{
			name: 'lmsEmbed',
			url: '',
			component: VbCenteredSpinnerComponent,
			allowAllAccess: true,
			resetZoom: true,
			resolve: [
				...initializationResolve,
				{
					token: 'videoId',
					resolveFn: () => queryParams.id
				},

				{
					token: 'password',
					resolveFn: () => queryParams.password
				},
				{
					token: 'initLmsMessageBus',
					deps: [LmsMessageBus, StateService, 'EmbedStartupResolve'],
					resolveFn: (LmsMessageBus: LmsMessageBus, $state: StateService) => {
						console.log('Sending embedReady message to LMS parent');
						publishParentMessage('embedReady', { context: { completionPercentage: parseInt(queryParams.completionPercentage, 10), lsmSaveIntervalInSec: BootstrapContext.lmsSaveIntervalInSec } });

						return firstValueFrom(
							LmsMessageBus.subscribeLmsParent()
								.pipe(
									filter(msg => msg && (msg.type === 'scormReady' || msg.type === 'scormInitializationFailed')),
									take(1)
								)
						).then(message => {
							if (message.type === 'scormReady') {
								let { startAt } = message.payload || {};
								startAt = startAt ? +startAt * 1000 : 0;
								queryParams.startAt = startAt;

								return;

							}
							$state.go('401', { reason: 'LmsInitializationFailed' }, { location: false });
							throw new Error('LMS initialization failed');

						});
					}
				}
			],
			redirectTo(transition: Transition) {
				const injector = transition.injector();
				return Promise.all([
					injector.getAsync('videoId'),
					injector.getAsync('password'),
					injector.getAsync('initLmsMessageBus')
				]).then(([videoId, password]) => {
					return {
						state: 'embed',
						params: { videoId, password }
					};
				});
			}
		}
	];
}



export function getEmbedVideoStates(stateName: string, includeInitResolve: boolean): any[] {
	return [{
		name: stateName,
		component: EmbedVideo,
		url: '',
		params: {
			videoId: null,
			password: null
		},
		allowAllAccess: true,
		redirectTo(transition: Transition) {
			const injector = transition.injector();

			return injector.getAsync('video')
				.then(() => undefined)
				.catch((errResponse: HttpErrorResponse) => {
					const reason = errResponse?.error?.reason;
					// if public video with invalid password, redirect to the password input state
					if (reason === 'InvalidPassword') {
						return injector.getAsync('videoId')
							.then(videoId => transition.router.stateService.target(PUBLIC_VIDEO_PASSWORD_STATE_NAME,
								{ videoId },
								{ location: false }));
					}
				});
		},
		resolve: [
			...(includeInitResolve ? initializationResolve : []),
			...getMainResolves(includeInitResolve),
			{
				token: 'password',
				deps: [Transition],
				resolveFn: (transition: Transition) => transition.params().password
			},
			{
				token: 'video',
				deps: [UserLocalIPService, VideoService, 'videoId', 'password', includeInitResolve ? 'EmbedStartupResolve' : undefined].filter(Boolean),
				resolveFn(userLocalIPService: UserLocalIPService, videoService: VideoService, videoId: string, password: string) {
					return userLocalIPService.checkUserLocation()
						.then(() => videoService.getVideoPlaybackEmbed(videoId, password))
						.catch((response: HttpErrorResponse) => {
							console.error('error in video', response);
							return Promise.reject(
								response?.error?.reason === 'EmbedNotAuthorized' ?
									{ ...response, error: { ...response.error, noLoginRedirect: true } } :
									response
							);
						});
				}
			}
		]
	}];
}

export function getPublicVideoPasswordState() {
	return {
		name: PUBLIC_VIDEO_PASSWORD_STATE_NAME,
		component: PublicVideoPasswordComponent,
		allowAllAccess: true,
		resetZoom: true,
		params: {
			videoId: {}
		},
		resolve: [
			{
				token: 'videoId',
				deps: [Transition],
				resolveFn: (transition: Transition) => transition.params().videoId
			},

			{
				token: 'cookieKey',
				deps: ['videoId'],
				resolveFn: (videoId: string) => `${videoId}-auth`
			},

			{
				token: 'videoInfo',
				deps: [VideoService, 'videoId'],
				resolveFn: (videoService: VideoService, videoId: string) => videoService.getVideoBasicInfo(videoId)
			},

			{
				token: 'loginRedirect',
				deps: [LoginRedirectService, StateService],
				resolveFn(loginRedirectService: LoginRedirectService, $state: StateService) {
					const videoRedirect = $state.href('embed', null).substring(1); // remove the leading #

					return () => loginRedirectService.redirectToLogin(videoRedirect);
				}
			},

			{
				token: 'submitRedirect',
				deps: [StateService],
				resolveFn: ($state: StateService) => (password?: string) => $state.go('embed', { password })
			}
		]
	};
}

function getEmbedPlaylistStates(): any[] {
	return [{
		name: 'embedPlaylist',
		url: '',
		component: UiViewContainerComponent,
		allowAllAccess: true,
		resetZoom: true,
		redirectTo(transition: Transition) {
			const layout = ['row', 'slider', 'grid'].find(l => queryParams.layout === l) || 'row';

			return transition.router.stateService.target('embedPlaylist.' + layout, {}, { location: false });
		},
		resolve: [
			...getMainResolves(),
			...initializationResolve,
			{
				token: 'playlist',
				deps: [UserLocalIPService, PlaylistService, 'playlistId', 'EmbedStartupResolve', 'mediaFeatures'],
				resolveFn(
					userLocalIPService: UserLocalIPService,
					playlistService: PlaylistService,
					playlistId: string,
					_EmbedStartup: any,
					mediaFeatures: IMediaFeatures // ensure media features are loaded before fetching the playlist
				) {
					if (!playlistId) {
						return;
					}

					// Backend filters videos with no access.
					// If no access to any of the videos, redirect to login
					return userLocalIPService.checkUserLocation()
						.then(() => playlistService.getPlaylist(playlistId))
						.then(playlist => {
							if (playlist.videos?.length > 0) {
								playlist.videos.forEach(v => v.chapterInfo = {});
								return playlist;
							}
							console.log('No videos in playlist');
							return Promise.reject({ status: 401 });
						});
				}
			}
		]
	},
	{
		name: 'embedPlaylist.row',
		component: EmbedPlaylistComponent,
		allowAllAccess: true
	},
	{
		name: 'embedPlaylist.slider',
		component: EmbedPlaylistSliderComponent,
		allowAllAccess: true

	},
	{
		name: 'embedPlaylist.grid',
		component: EmbedPlaylistGridComponent,
		allowAllAccess: true
	},
	{
		name: 'embedPlaylist.grid.play',
		component: EmbedVideo,
		allowAllAccess: true,
		params: { videoId: null, autoplay: null },
		resolve: [
			sessionResolve,
			{
				token: 'videoId',
				deps: [Transition],
				resolveFn: ($transition$: Transition) => $transition$.params().videoId
			},
			{
				token: 'params',
				deps: [Transition],
				resolveFn: ($transition$: Transition) => ({
					...queryParams,
					autoplay: $transition$.params().autoplay ?? queryParams.autoplay
				})
			},
			{
				token: 'video',
				deps: [VideoService, 'videoId'],
				resolveFn(videoService: VideoService, videoId: string) {
					return videoService.getVideoPlaybackEmbed(videoId);
				}
			}
		]
	}];
}

function getStates(): any[] {
	if(queryParams.playlist) {
		return getEmbedPlaylistStates();
	}

	if (queryParams.viewContext === 'lms_scorm') {
		return [
			...getLmsEmbedStates(),
			...getEmbedVideoStates('embed', false)
		];
	}
	return queryParams.fullPlayer
			? getEmbedFullVodPlaybackStates('embed')
			: getEmbedVideoStates('embed', true);
}

const basePortalResolves: ResolvableLiteral[] = [
	{
		token: 'customTheme',
		deps: [
			ThemeService
		],
		resolveFn: (ThemeService: ThemeService) => {
			if (!queryParams.defaultTheme) {
				return;
			}
			ThemeService.setCustomThemeMode({
				themeSettings: defaultColors
			});
		}
	}
];


function getEmbedFullVodPlaybackStates(stateName: string) {
	return [
		...getPortalBaseStates(UiViewContainerComponent, false, basePortalResolves, true),
		{
			name: stateName,
			url: '',
			allowAllAccess: true,
			component: EmbedFullVodPlaybackComponent,
			resolve: [
				{
					token: 'accountId',
					deps: [
						UserContextService
					],
					resolveFn: (UserContext: UserContextService) => {
						return UserContext.getAccount().id;
					}
				},
				...initializationResolve
			],
			loadChildren: () => {
				return import(/* webpackChunkName: 'portalVideoPlayback' */ 'rev-portal/media/videos/videoPlayback/VideoPlaybackAngular.Module')
					.then(mod => mod.VideoPlaybackAngularModule);
			},
			redirectTo(transition: Transition) {
				const injector = transition.injector();
				const videoId: string = BootstrapContext.videoId;
				const appBootstrapService = injector.get<AppBootstrapService>(AppBootstrapService);
				configureEmbedDisplay(appBootstrapService);

				return injector.getAsync('EmbedStartupResolve')
					.then(() => {
						return {
							state: PORTAL_VIDEO_PLAYBACK_STATE,
							params: {
								videoId,
								startAt: queryParams.startAt,
								autoplay: queryParams.autoplay,
								tab: queryParams.defaultSidebar,
								tabFilterQuery: queryParams.sidebarFilterQuery
							}
						};
					});
			}
		}
	];
}


export const EmbedStates: IVbNg2StateDeclaration[] = [
	...getCommonSecurityStatesNg2(initializationResolve, EmbedLoginComponent as any),

	{
		name: '401',
		url: '/401',
		component: Unauthorized401Component,
		resolve: [
			...initializationResolve,
			{
				token: 'reason',
				deps: [Transition],
				resolveFn: (transition: Transition) => transition.params().reason
			}
		],
		allowAllAccess: true,
		params: params401
	},
	...getStates(),
	getPublicVideoPasswordState()
];

export function configureEmbedDisplay(AppBootstrapService: AppBootstrapService): void {
	if (!queryParams.fullPlayer) {
		return;
	}

	const videoPlayerConfig: IVideoPlaybackConfig = {
		isEmbedFullPlayer: true,
		showApprovalProcess: false,
		showSettingsMenu: false,
		mobileLayoutBreakPointSize: queryParams.mobileLayoutBreakPoint,
		sidebarPanelWidthInPx: '350px',
		hidePlaybackTitleHeader: true,
		playerPadding: 0,
		sidebarDisplay: {
			hideInfo: parseBooleanQueryParam(queryParams.hideInfo),
			hideComments: parseBooleanQueryParam(queryParams.hideComments),
			hidePulse: parseBooleanQueryParam(queryParams.hidePulse),
			hideReview: parseBooleanQueryParam(queryParams.hideReview),
			hideVideoAssistant: parseBooleanQueryParam(queryParams.hideVideoAssistant),
			hidePlaylist: parseBooleanQueryParam(queryParams.hidePlaylist),
			hideChapters: parseBooleanQueryParam(queryParams.hideChapters),
			hideAnalytics: parseBooleanQueryParam(queryParams.hideAnalytics),
			smallButtons: true
		},
		playInLoop: parseBooleanQueryParam(queryParams.loopVideo),
		playerConfig: getEmbedPlayerConfig(queryParams, true),
		disableDualPlayback: true,
		disableUserPreferenceStore: true,
		analyticsViewContext: queryParams.viewContext,
		forceMute: parseBooleanQueryParam(queryParams.noVolumeControl)
	};

	AppBootstrapService.setAppConfig({ videoPlayerConfig });
}
